import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, input, signal} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AbstractControl, FormArray, FormControl, FormGroup, ReactiveFormsModule, ValidatorFn} from "@angular/forms";
import {NgClass} from "@angular/common";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatIconModule} from "@angular/material/icon";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatButtonModule} from "@angular/material/button";
import {MatSlideToggleModule} from "@angular/material/slide-toggle";
import {MatMenuModule} from "@angular/material/menu";
import {erTomListe, siste} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeTomStreng, erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {EksternMøtedeltakerModell, EksternMøtedeltakerSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {TranslateModule} from "@ngx-translate/core";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {ErrorStateMatcher} from "@angular/material/core";
import {VisAlltidErrorStateMatcher} from "felles-ng2/infrastruktur/material/VisAlltidErrorStateMatcher";
import {erTomtObjekt} from "felles-ng2/støttefunksjoner/objekter";
import {oppdaterGuiVedEndringIFormArrayLengde, skjemaendringerSomObserverbar} from "felles-ng2/infrastruktur/angular/reactiveForm";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

const debug = initDebugger('MøteRedigerEksterneDeltakereKontroller');

export type EksterneDeltakereSkjemaType = ReturnType<typeof MøteRedigerEksterneDeltakereKontroller.opprettSkjema>;
export type EksterneDeltakereSkjemaVerdier = EksterneDeltakereSkjemaType['value'];
export type EksternDeltakerSkjemaType = ReturnType<typeof MøteRedigerEksterneDeltakereKontroller.opprettSkjemarad>;
export type EksternDeltakerSkjemaVerdier = EksternDeltakerSkjemaType['value'];

@Component({
    selector: 'moete-rediger-eksterne-deltakere',
    templateUrl: './møte-rediger-eksterne-deltakere.html',
    styleUrls: ['./møte-rediger-eksterne-deltakere.scss'],
    providers: [
        //for at valideringsfeil på navn skal vises selvom bruker ikke har rørt dette feltet
        { provide: ErrorStateMatcher, useClass: VisAlltidErrorStateMatcher }
    ],
    imports: [
        NgClass,
        ReactiveFormsModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        TipsKomponent,
        MatSlideToggleModule,
        MatInputModule,
        MatMenuModule,
        TranslateModule,
        StandardValideringsfeilPipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MøteRedigerEksterneDeltakereKontroller {

    /** Oppretter nytt tomt skjema */
    static opprettSkjema() {
        return new FormArray<EksternDeltakerSkjemaType>([]);
    }

    /** Nullstiller angitt skjema og  populerer det med angitte eksterne deltakere */
    static repopulerSkjema(skjema: EksterneDeltakereSkjemaType, eksterne: EksternMøtedeltakerModell[]) {
        skjema.clear({emitEvent: false});
        eksterne
          .map(ekstern => MøteRedigerEksterneDeltakereKontroller.opprettSkjemarad(ekstern))
          .forEach(skjemarad => skjema.push(skjemarad, {emitEvent: false}));
        //først nå emitter vi en valueChanges event (foregående clear og push kall har ikke ført til eventer)
        skjema.updateValueAndValidity();
    }

    static opprettSkjemarad(verdier = {id: nyUuid(), navn: '', rolle: '', kommentar: ''}) {
        return new FormGroup({
            id: new FormControl(verdier.id, {nonNullable: true}), //ikke eksponert i gui
            navn: new FormControl(verdier.navn, {validators: FeltValidatorer.litenIkkeTomStreng, nonNullable: true}),
            rolle: new FormControl(verdier.rolle, {validators: FeltValidatorer.litenStreng, nonNullable: true}),
            kommentar: new FormControl(verdier.kommentar, {validators: FeltValidatorer.litenStreng, nonNullable: true}),
            slettet: new FormControl(false, {nonNullable: true})
        }, {validators: [validerRad()]});
    }

    static somEksternMøtedeltakerSpesifikasjon(eksterneDeltakere: EksterneDeltakereSkjemaVerdier): EksternMøtedeltakerSpesifikasjon[] {
        return eksterneDeltakere
          //merk at slettede rader ikke trengs å filtreres bort eksplisitt - de blir automatisk utelatt da de er markert med FormGroup.disable()
          .filter(deltaker => erIkkeTomRad(deltaker))
          .map(deltaker => ({
              id: deltaker.id!,
              navn: deltaker.navn!,
              rolle: deltaker.rolle!,
              kommentar: deltaker.kommentar!
          }))
    }

    skjema = input.required<EksterneDeltakereSkjemaType>();

    skjemaVises = signal(false);
    kunTommeRader = signal(true);

    constructor(private destroyRef: DestroyRef, cdRef: ChangeDetectorRef) {
        oppdaterGuiVedEndringIFormArrayLengde(this.skjema, cdRef);

        //reager på endringer i skjema
        skjemaendringerSomObserverbar(this.skjema).pipe(takeUntilDestroyed())
          .subscribe(rader => {
            //vis skjema dersom ikke tomt
            this.skjemaVises.set(rader.length > 0);
            this.kunTommeRader.set(rader.every(rad => !erIkkeTomRad(rad)));

            //legg til ny rad dersom siste rad er ikke-tom
            if (erTomListe(rader) || erIkkeTomRad(siste(rader)!))
                this.leggTilTomRad();
        })
    }

    visSkjema() {
        this.skjemaVises.set(true);
    }

    skjulSkjema() {
        this.skjemaVises.set(false);
    }

    slett(radnummer: number) {
        const rad = this.skjema().at(radnummer);
        rad.disable();
        //dersom rad ikke er tom viser vi "angre slett" knapp
        if (erIkkeTomRad(rad.value))
            rad.controls.slettet.setValue(true);
        else
            this.skjema().removeAt(radnummer);
    }

    angreSlett(radnummer: number) {
        const rad = this.skjema().at(radnummer);
        rad.enable();
        rad.controls.slettet.setValue(false);
    }

    // private hjelpemetoder

    private leggTilTomRad() {
        this.skjema().push(MøteRedigerEksterneDeltakereKontroller.opprettSkjemarad());
    }
}

function erIkkeTomRad(rad: EksternDeltakerSkjemaVerdier) {
    return erIkkeTomStreng(rad.navn) || erIkkeTomStreng(rad.rolle) || erIkkeTomStreng(rad.kommentar);
}

/** Markerer navn som required dersom navn er tomt og enten rolle eller kommentar er ikke-tomme */
function validerRad(): ValidatorFn {
    return (skjemakontroll: AbstractControl) => {
        const rad = <EksternDeltakerSkjemaType>skjemakontroll;
        const feilPåNavn = {...rad.controls.navn.errors}
        if (erTomEllerNil(rad.controls.rolle.value) && erTomEllerNil(rad.controls.kommentar.value))
            delete feilPåNavn.required;

        else if (erTomEllerNil(rad.controls.navn.value))
            feilPåNavn.required = true;

        rad.controls.navn.setErrors(erTomtObjekt(feilPåNavn) ? null : feilPåNavn);
        return null;
    }
}
